<script setup lang="ts">
import { provide, watch, reactive, computed, useAttrs } from 'vue';
import { BButton } from 'bootstrap-vue-next';
import { TableModel } from './model';
import type {
  TableOptions,
  IDataTableHandler,
  IColumnDefinition,
  RowData,
  RowGroup,
  ROW_DETAILS_MODE,
  HEADER_MODE,
  COLUMN_HEADER_STYLE,
  ALIGNMENT,
  GetRowClass,
  GetCellClass,
  CheckRowEditable,
  CheckRowDeletable,
} from './types';
import { useDataTableHandler } from './composables';
import { normalizeColumns } from './normalize-columns';
import ItpDataTableHeader from './ItpDataTableHeader.vue';
import ItpDataTableBody from './ItpDataTableBody.vue';
import { dataTableMessages, loadingMessages } from '../../messages';

const props = withDefaults(
  defineProps<{
    rows?: RowData[];
    columns: IColumnDefinition[];
    groups?: RowGroup[] | null;

    rowKeyField?: string;
    groupKeyField?: string;
    useIndexAsRowKey?: boolean;

    tableId?: string;
    paging?: boolean;
    pageSize?: number;
    editable?: boolean;
    sortable?: boolean;
    rowSelection?: boolean;
    rowHeader?: boolean;
    footerTotals?: boolean;
    canCreateRow?: boolean;
    fillEmptyRowSpace?: boolean;
    variant?: string;
    tableClass?: string | object;

    lang?: string;
    rowDetailsMode?: ROW_DETAILS_MODE;
    headerMode?: HEADER_MODE;
    columnHeaderStyle?: COLUMN_HEADER_STYLE;
    textAlign?: ALIGNMENT;
    headerTextAlign?: ALIGNMENT;
    rowClassName?: string | GetRowClass;
    rowTemplate?: Record<string, any>;
    messages?: Record<string, string>;

    noDeleteRow?: boolean;
    noConfirmDeleteRow?: boolean;
    noExpandableRowDetails?: boolean;

    loading?: boolean;
    handler?: IDataTableHandler;

    globs?: Record<string, any>;

    value?: RowData[];
    rowKey?: string;
    noCreateRow?: boolean;
    cellClass?: string | GetCellClass;
    compact?: boolean;
    prependNewRow?: boolean;
    isRowEditable?: CheckRowEditable;
    isRowDeletable?: CheckRowDeletable;
    showHeader?: boolean;
    hidden?: boolean;
  }>(),
  {
    rows: undefined,
    groups: null,
    rowKeyField: undefined,
    groupKeyField: 'group_id',
    useIndexAsRowKey: false,
    paging: false,
    pageSize: 10,
    editable: true,
    sortable: true,
    rowSelection: false,
    rowHeader: true,
    footerTotals: false,
    canCreateRow: undefined,
    fillEmptyRowSpace: true,
    columnHeaderStyle: 'tableHeader',
    textAlign: 'left',
    noDeleteRow: false,
    noConfirmDeleteRow: false,
    noExpandableRowDetails: false,
    variant: undefined,
    value: undefined,
    rowKey: undefined,
    noCreateRow: undefined,
    compact: false,
    prependNewRow: false,
    showHeader: true,
    hidden: false,
  },
);

const effectiveRows = computed(() => props.rows ?? props.value ?? []);
const effectiveRowKeyField = computed(
  () => props.rowKeyField ?? props.rowKey ?? 'id',
);
const effectiveCanCreateRow = computed(() => {
  if (!props.editable) {
    return false;
  }
  if (props.canCreateRow !== undefined) {
    return props.canCreateRow;
  }
  if (props.noCreateRow !== undefined) {
    return !props.noCreateRow;
  }
  return true;
});

const emit = defineEmits([
  'selection-change',
  'save-completed',
  'delete-completed',
  'focus-cell',
  'selectedRowsChanged',
]);

const createEventPayload = (row: any) => {
  let waitForPromise: Promise<any> | undefined;

  return {
    row,
    waitFor: (promise: Promise<any>) => {
      waitForPromise = promise;
      return promise;
    },
    getWaitForPromise: () => waitForPromise,
  };
};

async function awaitEventPayload(
  payload: ReturnType<typeof createEventPayload>,
): Promise<any> {
  const promise = payload.getWaitForPromise();
  if (promise) {
    return await promise;
  }
  return undefined;
}

const attrs = useAttrs();

function invokeHandler(eventName: string, payload: any): any {
  const candidates = [
    `on${eventName.charAt(0).toUpperCase()}${eventName.slice(1)}`,
    `on${eventName
      .replace(/[-:_]([a-z])/g, (_, character) => character.toUpperCase())
      .replace(/^./, (character) => character.toUpperCase())}`,
  ];
  for (const key of candidates) {
    const handler = (attrs as Record<string, unknown>)[key];
    if (typeof handler === 'function') {
      return (handler as (payload: any) => any)(payload);
    }
  }
  return undefined;
}

const defaultHandler = useDataTableHandler({
  onDelete: async (row) => {
    const payload = createEventPayload(row);
    await invokeHandler('delete', payload);
    await awaitEventPayload(payload);
    return true;
  },
  onDeleteCompleted: (row) => {
    emit('delete-completed', { row });
  },
  onSave: async (row) => {
    const payload = createEventPayload(row);
    await invokeHandler('save', payload);
    const savedData = await awaitEventPayload(payload);
    return savedData || row;
  },
  onSaveCompleted: (row, result) => {
    emit('save-completed', { row, result });
  },
  onSelectionChange: (payload) => {
    emit('selectedRowsChanged', payload);
  },
  onCellEvent: (event, payload) => {
    invokeHandler(event, payload);
  },
});

const activeHandler: IDataTableHandler = props.handler || defaultHandler;

const buildOptions = (): TableOptions => ({
  columns: normalizeColumns(props.columns),
  rows: effectiveRows.value,
  groups: props.groups,
  rowKeyField: effectiveRowKeyField.value,
  groupKeyField: props.groupKeyField,
  useIndexAsRowKey: props.useIndexAsRowKey,
  tableId: props.tableId,
  paging: props.paging,
  pageSize: props.pageSize,
  editable: props.editable,
  sortable: props.sortable,
  rowSelection: props.rowSelection,
  rowHeader: props.rowHeader,
  footerTotals: props.footerTotals,
  lang: props.lang,
  rowDetailsMode: props.rowDetailsMode,
  headerMode: props.headerMode,
  columnHeaderStyle: props.columnHeaderStyle,
  textAlign: props.textAlign,
  headerTextAlign: props.headerTextAlign,
  messages: props.messages,
  canCreateRow: effectiveCanCreateRow.value,
  rowTemplate: props.rowTemplate,
  fillEmptyRowSpace: props.fillEmptyRowSpace,
  rowClassName: props.rowClassName,
  noDeleteRow: props.noDeleteRow,
  noConfirmDeleteRow: props.noConfirmDeleteRow,
  noExpandableRowDetails: props.noExpandableRowDetails,
  isRowEditable: props.isRowEditable,
  isRowDeletable: props.isRowDeletable,
  globs: props.globs,
});

const mutableOptions = reactive(buildOptions());

const model = new TableModel(mutableOptions, activeHandler);
provide('model', model);

const wrapperClasses = computed(() => {
  const classes = ['itp-datatable'];
  if (props.variant) {
    classes.push(`variant-${props.variant}`);
  }
  return classes;
});

const loadingLabel = computed(() => loadingMessages.loading);

const isBusy = computed(() => props.loading || model.commitPending.value);

const tableClasses = computed(() => {
  const classes: (string | object)[] = [
    'itp-datatable__table',
    'table',
    'mb-0',
  ];
  if (props.variant) {
    classes.push(`variant-${props.variant}`);
  }
  if (props.tableClass) {
    classes.push(props.tableClass);
  }
  return classes;
});

watch(
  () => props,
  () => {
    const savedWidths = new Map<string, string>();
    for (const col of mutableOptions.columns) {
      if (col.width) {
        savedWidths.set(col.field, col.width);
      }
    }

    Object.assign(mutableOptions, buildOptions());

    for (const col of mutableOptions.columns) {
      const saved = savedWidths.get(col.field);
      if (saved) {
        col.width = saved;
      }
    }
  },
  { deep: true },
);

watch([effectiveRows, () => effectiveRows.value?.length], ([newRows]) => {
  if (newRows) {
    model.updateRows(newRows);
  }
});

watch(
  () => props.paging,
  (newVal) => {
    model.setPaging(newVal);
  },
);

watch(
  () => props.pageSize,
  (newVal) => {
    if (newVal) {
      model.pager.pageSize.value = newVal;
    }
  },
);

watch(
  () => props.columns,
  (newCols) => {
    if (model.options.columns && newCols) {
      newCols.forEach((newCol) => {
        const oldCol = model.options.columns.find(
          (c) => c.field === newCol.field,
        );
        if (oldCol && oldCol.width) {
          newCol.width = oldCol.width;
        }
      });
    }
  },
  { deep: true },
);
</script>

<template>
  <div
    v-show="!hidden"
    class="position-relative"
    :class="[
      wrapperClasses,
      { 'itp-datatable--loading': isBusy, 'itp-datatable--compact': compact },
    ]"
    :aria-busy="isBusy"
  >
    <div class="itp-datatable__table-wrapper">
      <table :class="tableClasses">
        <ItpDataTableHeader v-if="showHeader" />
        <ItpDataTableBody>
          <template #rowDetails="{ row }">
            <slot name="rowDetails" :row="row" />
          </template>
          <template #rowGroupHeader="{ group, rows }">
            <slot name="rowGroupHeader" :group="group" :rows="rows" />
          </template>
          <template #afterLastRow>
            <slot name="afterLastRow" :table="model" />
          </template>
        </ItpDataTableBody>
      </table>
    </div>

    <div v-if="model.pager.enabled.value" class="itp-datatable__footer">
      <div class="text-muted small ms-2">
        {{ dataTableMessages.total }}:
        <strong>{{ model.pager.totalSize.value }}</strong>
      </div>
      <div class="d-flex gap-1 me-2">
        <BButton
          size="sm"
          variant="outline-secondary"
          :disabled="model.pager.currentPage.value <= 1"
          @click="model.prevPage()"
        >
          <iconify-icon noobserver icon="mdi:chevron-left" />
        </BButton>
        <span class="px-2 align-self-center">{{
          model.pager.currentPage.value
        }}</span>
        <BButton
          size="sm"
          variant="outline-secondary"
          :disabled="
            model.pager.currentPage.value * model.pager.pageSize.value >=
            model.pager.totalSize.value
          "
          @click="model.nextPage()"
        >
          <iconify-icon noobserver icon="mdi:chevron-right" />
        </BButton>
      </div>
    </div>

    <div
      v-if="isBusy"
      class="itp-datatable__progress"
      role="status"
      aria-live="polite"
    >
      <span class="visually-hidden">{{ loadingLabel }}</span>
    </div>
  </div>
</template>
